/// <amd-module name="Repos/Views/Settings/Branches/Components/SharedFocusTreeRow/ShareFocusTreeRow" />
import React = require("react");
import { css } from "VSS/Platform/Layout";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { getDefaultLinkProps, ILinkProps } from "VSSUI/Link";
import { ITreeRowDetails } from "VSSUI/TreeEx";
import { KeyCode } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";

interface IShareFocusTreeRowProps<T> {
    className?: string;
    details: ITreeRowDetails<T>;
    focusZoneId?: string;
    index: number;
    indexOffset?: number;
    linkProps: ILinkProps;
}

export class ShareFocusTreeRow<T> extends React.Component<IShareFocusTreeRowProps<T>> {
    private rowElement = React.createRef<HTMLTableRowElement | HTMLAnchorElement>();

    public render(): JSX.Element {
        const { details, index, focusZoneId, linkProps } = this.props;
        const { ariaRowOffset, data, excludeFocusZone, renderSpacer, selectableText, selection, singleClickActivation } = details;

        return (
            <FocusWithin onFocus={this.onFocus}>
                {(focusStatus: IFocusWithinStatus) => {
                    return (
                        <a
                            aria-busy={data === undefined}
                            aria-expanded={data && data.underlyingItem.expanded}
                            aria-selected={selection && selection.selected(index) ? true : undefined}
                            className={css(
                                this.props.className,
                                "bolt-tree-row bolt-table-row bolt-list-row",
                                index === 0 && "first-row",
                                focusStatus.hasFocus && "focused",
                                selection && selection.selected(index) && "selected",
                                singleClickActivation && "single-click-activation",
                                selectableText && "selectable-text",
                                linkProps && "v-align-middle"
                            )}
                            data-focuszone={excludeFocusZone || (selection && !selection.selectable(index)) ? undefined : focusZoneId}
                            data-row-index={index}
                            tabIndex={!this.props.indexOffset ? getTabIndex(details) : -1}
                            onBlur={focusStatus.onBlur}
                            onFocus={focusStatus.onFocus}
                            onKeyDown={this.onKeyDown}
                            ref={this.rowElement as any}
                            {...getDefaultLinkProps(linkProps)}
                            role="row"
                        >
                            <td
                                key="left-spacer"
                                className="bolt-table-cell-compact bolt-table-cell bolt-list-cell"
                                role="presentation"
                                aria-hidden={true}
                            >
                                {renderSpacer && renderSpacer(index, true)}
                            </td>
                            {this.props.children}
                            <td
                                key="right-spacer"
                                className="bolt-table-cell-compact bolt-table-cell bolt-list-cell"
                                role="presentation"
                                aria-hidden={true}
                            >
                                {renderSpacer && renderSpacer(index, false)}
                            </td>
                        </a>
                    );
                }}
            </FocusWithin>
        );
    }

    private onFocus = (event: React.FocusEvent<HTMLElement>) => {
        this.props.details.onFocusItem(this.props.index, event);
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (this.rowElement.current === event.nativeEvent.srcElement) {
                const { data } = this.props.details;
                if (data) {
                    if (data.onToggle) {
                        const { expanded } = data.underlyingItem;
                        if ((event.which === KeyCode.rightArrow && !expanded) || (event.which === KeyCode.leftArrow && expanded)) {
                            data.onToggle(event, data);
                            event.preventDefault();
                        }
                    }
                }
            }
        }
    };
}
